// src/api/http.js
import { invoke } from '@tauri-apps/api/core';

// 环境检测
const appEnv = process.env.VUE_APP_ENV;
const environment = (appEnv === 'production') ? 'production' : 'development';

// 域名配置（根据环境切换）
const BASE_DOMAIN = environment === 'production' ? 'https://ai.xinfushenghuo.cn' : 'https://aitest.xinfushenghuo.cn';

// ==================== API 配置 - 支持多个域名 ====================
const API_CONFIG = {
  login: {
    baseURL: 'https://loginf.lawxp.com/', // `${BASE_DOMAIN}/loginapi/`,
    endpoints: {
      verificationLogin: 'Auth/GetTokenByPhone',
      accountLogin: 'Auth/GteTokenByAccountPwd',
      getCodeImg: 'Auth/GetCodeImg',
      sendVerificationCode: 'Auth/APPSendCode',
      getUserInfo: 'F_UserInfo/GetUserInfoByToken'
    }
  },
  tuansudi: {
    baseURL: 'https://api.tuansudi.com/', // `${BASE_DOMAIN}/tuansudiapi/`,
    endpoints: {
      getIP: 'api/Synthetical/GetIP'
    }
  },
  xinfushenghuo: {
    baseURL: 'https://api1.xinfushenghuo.cn/',
    endpoints: {
      getAgreement: 'apishop/article/info',
      getPrivacy: 'apishop/article/info'
    }
  },
  im: {
    baseURL: 'https://im.xinfushenghuo.cn/',
    endpoints: {
      userRegister: 'j_user/userRegister'
    }
  }
};

// ==================== 拦截器系统 ====================
const requestInterceptors = [];
const responseInterceptors = [];

export function addRequestInterceptor(fn) {
  requestInterceptors.push(fn);
}

export function addResponseInterceptor(fn) {
  responseInterceptors.push(fn);
}

// ==================== 工具函数：标准化 Headers ====================
/**
 * 标准化 headers：统一小写键名，合并时用户传入的优先
 * @param {Object} userHeaders - 用户传入的 headers
 * @param {Object} defaultHeaders - 默认 headers
 * @returns {Object} 标准化后的 headers
 */
function normalizeHeaders(userHeaders = {}, defaultHeaders = {}) {
  const result = {};
  
  // 1. 先添加默认值（作为基础）
  Object.entries(defaultHeaders).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      result[key.toLowerCase()] = value;
    }
  });
  
  // 2. 用户传入的覆盖默认值（确保用户配置优先）
  Object.entries(userHeaders).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      result[key.toLowerCase()] = value;
    }
  });
  
  return result;
}

// ==================== 核心请求函数（IPC 方式） ====================
/**
 * 通过 Rust IPC 发起 HTTP 请求（完全绕过 CORS）
 * @param {Object} options
 * @param {string} options.url - 接口路径（相对路径）
 * @param {string} [options.method='GET']
 * @param {Object} [options.headers={}]
 * @param {any} [options.data=null] - 请求体
 * @param {Object} [options.params={}] - 查询参数
 * @param {string} [options.service=null] - 使用 API_CONFIG 中的服务配置
 * @param {string} [options.baseURL=null] - 自定义 baseURL
 * @param {number} [options.timeout=60] - 超时时间（秒，在 Rust 层生效）
 */
export async function tauriHttp(options) {
  const {
    url,
    method = 'GET',
    headers = {},
    data = null,
    params = {},
    service = null,
    baseURL = null,
    timeout = 60  // 秒，传给 Rust 层
  } = options;

  // 1. 构建完整 URL
  let fullUrl;
  if (url.startsWith('http')) {
    fullUrl = url;
  } else if (service && API_CONFIG[service]) {
    fullUrl = `${API_CONFIG[service].baseURL}${url}`;
  } else if (baseURL) {
    fullUrl = `${baseURL}${url}`;
  } else {
    throw new Error('必须指定 service 或 baseURL');
  }

  // 2. 拼接查询参数
  if (Object.keys(params).length > 0) {
    try {
      const urlObj = new URL(fullUrl);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          urlObj.searchParams.append(k, v);
        }
      });
      fullUrl = urlObj.toString();
    } catch {
      // 降级：手动拼接
      const queryString = Object.entries(params)
        .filter(([_, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      fullUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}${queryString}`;
    }
  }

  // 3. 构建请求配置
  let config = { url: fullUrl, method, headers, data, params };

  // 4. 执行请求拦截器（可修改 headers、data 等）
  for (const fn of requestInterceptors) {
    try {
      config = fn(config) || config;
    } catch (err) {
      console.error('[请求拦截器失败]', err);
      throw err;
    }
  }

  // 🔑 关键：标准化 headers（用户传入的优先，统一小写）
  const defaultHeaders = {};
  // 只有当用户没传 Content-Type 且有 body 时，才设置默认值
  const hasUserContentType = Object.keys(config.headers).some(
    k => k.toLowerCase() === 'content-type'
  );
  if (!hasUserContentType && config.data) {
    defaultHeaders['Content-Type'] = 'application/json;charset=UTF-8';
  }
  
  const finalHeaders = normalizeHeaders(config.headers, defaultHeaders);

  console.log('[IPC HTTP 请求]', {
    url: config.url,
    method: config.method,
    service,
    headers: finalHeaders,  // 🔍 打印 headers 调试
    hasBody: !!config.data
  });

  // 5. 通过 IPC 调用 Rust 层发起请求
  try {
    console.log('[IPC HTTP] 发起请求:', config.url);

    // 🔑 关键：参数名必须是 `req`（与 Rust 函数签名一致）
    const response = await invoke('http_request', {
      req: {
        url: config.url,
        method: config.method.toUpperCase(),
        headers: finalHeaders,  // ✅ 使用标准化后的 headers
        body: config.data ? (
          typeof config.data === 'string' 
            ? config.data 
            : JSON.stringify(config.data)
        ) : null,
        timeout
      }
    });

    console.log('[IPC HTTP] 响应状态:', response.status);

    // 6. 解析响应体（兼容 JSON / XML / 纯文本）
    let resData;
    const contentType = response.headers?.['content-type'] || '';
    
    try {
      if (contentType.includes('application/json') || contentType.includes('text/json')) {
        resData = JSON.parse(response.body);
      } else {
        // 尝试解析 JSON，失败则返回原始文本（支持 XML）
        try {
          resData = JSON.parse(response.body);
        } catch {
          resData = { 
            _raw: response.body,
            _contentType: contentType
          };
        }
      }
    } catch (parseErr) {
      console.warn('[响应解析失败，返回原始文本]', parseErr);
      resData = { _raw: response.body, _contentType: contentType };
    }

    // 7. 构建统一返回格式（参考 Electron 风格）
    const code = resData?.code;
    const isCodeValid = code === 10000 || code === 0 || code === 200;
    
    const result = {
      success: response.status >= 200 && response.status < 300 && (resData?.code === undefined || isCodeValid),
      data: resData,
      status: response.status,
      headers: response.headers || {}
    };

    // 8. 执行响应拦截器
    let finalData = result;
    for (const fn of responseInterceptors) {
      try {
        finalData = fn(finalData) || finalData;
      } catch (err) {
        console.error('[响应拦截器失败]', err);
        throw err;
      }
    }

    // 9. HTTP 状态码错误处理
    if (response.status < 200 || response.status >= 300) {
      const errorMsg = resData?.message || resData?.msg || response.body?.substring(0, 100) || 'HTTP Error';
      throw new Error(`HTTP ${response.status}: ${errorMsg}`);
    }

    console.log('[IPC HTTP 成功]', {
      url: config.url,
      status: response.status,
      success: result.success
    });

    return finalData;

  } catch (err) {
    console.error('[IPC HTTP 请求失败]', {
      url: fullUrl,
      error: err?.message || 'Unknown error',
      stack: err?.stack,
      rustError: err
    });

    // 返回兼容 Electron 的错误格式，避免前端崩溃
    return {
      success: false,
      error: err?.message || '获取数据失败，请稍后重试',
      data: null,
      status: err?.status || 0
    };
  }
}

// ==================== 业务方法封装 ====================

// 获取 IP 地址（XML 接口）
export const getIP = () => {
  return tauriHttp({
    service: 'tuansudi',
    url: 'api/Synthetical/GetIP',
    method: 'GET'
    // 不设置 Content-Type，让服务端决定返回格式
  });
};

// 获取协议（用户协议/隐私协议）
export const getAgreement = (code) => {
  return tauriHttp({
    service: 'xinfushenghuo',
    url: 'apishop/article/info',
    method: 'GET',
    params: { code, id: 0 }
  });
};

export const getPrivacy = (code) => {
  return getAgreement(code);
};

// 验证码登录
export const verificationLogin = (params) => {
  return tauriHttp({
    service: 'login',
    url: 'Auth/GetTokenByPhone',
    method: 'POST',
    data: params
  });
};

// 账号密码登录
export const accountLogin = (params) => {
  return tauriHttp({
    service: 'login',
    url: 'Auth/GteTokenByAccountPwd',
    method: 'POST',
    data: params
  });
};

// 获取图形验证码
export const getCodeImg = (imgKey) => {
  return tauriHttp({
    service: 'login',
    url: 'Auth/GetCodeImg',
    method: 'GET',
    params: { ImgKey: imgKey }
  });
};

// 发送验证码
export const sendVerificationCode = (data) => {
  return tauriHttp({
    service: 'login',
    url: 'Auth/APPSendCode',
    method: 'POST',
    data
  });
};

// 获取用户信息
export const getUserInfo = (params, headers = {}) => {
  return tauriHttp({
    service: 'login',
    url: 'F_UserInfo/GetUserInfoByToken',
    method: 'POST',
    data: params,
    headers
  });
};

// 用户注册
export const userRegister = (data, headers = {}) => {
  return tauriHttp({
    service: 'im',
    url: 'j_user/userRegister',
    method: 'POST',
    data,
    headers
  });
};

// 重置密码
export const setPassword = (data) => {
  return tauriHttp({
    service: 'login',
    url: 'Auth/SetPwd',
    method: 'POST',
    data
  });
};

// ==================== 默认拦截器（可选） ====================

// Token 自动注入
addRequestInterceptor((config) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// 401 自动登出
addResponseInterceptor((result) => {
  if (result.status === 401) {
    console.warn('[Token 失效，自动清理]');
    if (typeof window !== 'undefined') {
      localStorage.removeItem('token');
      // 可触发登出事件
      window.dispatchEvent?.(new CustomEvent('auth:invalid'));
    }
  }
  return result;
});

// ==================== 导出 ====================
export { API_CONFIG };
export default tauriHttp;